import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";
import { IVendorFullData } from "./getVendorsFullData";

export interface IUpdateVendorParams {
	vendorId: number;
	name: string;
	url: string;
	vendorParentId?: number;
}

interface IUpdateVendorState {
	loading: boolean;
	error: string | null;
	data: IVendorFullData | null;
}

const initialState: IUpdateVendorState = {
	loading: false,
	error: null,
	data: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const buildUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Vendors/full-data`;
};

export const updateVendor = createAsyncThunk<IVendorFullData, IUpdateVendorParams>(
	"passwordManager/updateVendor",
	async ({ vendorId, name, url, vendorParentId }, { rejectWithValue, dispatch }) => {
		try {
			const body = {
				vendorId,
				name,
				url,
				...(vendorParentId !== undefined ? { vendorParentId } : {}),
			};

			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.put<IVendorFullData | Array<IVendorFullData>>(buildUrl(), body, { headers: getAuthHeaders() }),
			);

			const payload = res.data;
			if (Array.isArray(payload)) {
				if (!payload.length) throw new Error("Unable to parse vendor response");
				return payload[0];
			}
			if (!payload || typeof payload !== "object") {
				throw new Error("Unable to parse vendor response");
			}
			return payload;
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				const responseData = error.response?.data as { message?: string; title?: string } | string | undefined;
				const apiMessage =
					typeof responseData === "string" ? responseData : responseData?.message || responseData?.title || undefined;
				if (apiMessage) return rejectWithValue(apiMessage);
			}
			const message = error instanceof Error ? error.message : "Failed to update vendor";
			return rejectWithValue(message);
		}
	},
);

const updateVendorSlice = createSlice({
	name: "passwordManager/updateVendor",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(updateVendor.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateVendor.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.data = action.payload;
			})
			.addCase(updateVendor.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to update vendor";
			});
	},
});

export const updateVendorReducer = updateVendorSlice.reducer;
